'use client';

import { cssVars } from '@/styles/theme';
import { useLocale } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface DropdownProps {
  trigger: React.ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
}

export default function Dropdown({ trigger, items, align = 'left' }: DropdownProps) {
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="cursor-pointer"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {trigger}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute top-full z-50 mt-2 min-w-[200px] rounded-2xl border-2 py-2 shadow-lg ${
            align === 'right' ? 'end-0' : 'start-0'
          }`}
          style={{
            backgroundColor: cssVars.neutral.surface,
            borderColor: cssVars.neutral.border,
          }}
        >
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`flex w-full items-center gap-3 px-4 py-3 text-start transition-all ${
                  item.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-opacity-50'
                }`}
                style={{
                  backgroundColor: item.disabled ? 'transparent' : 'transparent',
                  color: item.danger ? cssVars.status.error : cssVars.neutral.text,
                }}
                onMouseEnter={(e) => {
                  if (!item.disabled) {
                    e.currentTarget.style.backgroundColor = `color-mix(in srgb, ${cssVars.primary.DEFAULT} 10%, transparent)`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {Icon && <Icon className="h-5 w-5" />}
                <span className="font-semibold">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
